import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BarcodeDetector } from 'barcode-detector';
import { WebcamComponent, WebcamImage, WebcamModule } from 'ngx-webcam';
import { debounceTime, distinctUntilChanged, first, scan, Subject, Subscription } from 'rxjs';

import {
  ElementLoadingIndicatorComponent,
  safeDetectChanges,
  TooltipDirective,
} from '@blaze-shared/shared-ui';
import { isDeepEqual } from '@blaze-shared/shared-util';

@Component({
  selector: 'be-barcode-scanner-overlay',
  imports: [ElementLoadingIndicatorComponent, TooltipDirective, WebcamModule],
  templateUrl: './barcode-scanner-overlay.component.html',
  styleUrls: ['./barcode-scanner-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarcodeScannerOverlayComponent implements OnDestroy, OnInit {
  private cdr = inject(ChangeDetectorRef);

  @ViewChild('webcam') webcam?: WebcamComponent;

  private detectedBarcodes$$ = new Subject<string[]>();
  private detectSub?: Subscription;

  close$$ = new Subject<boolean>();
  detectedBarcodes: string[] = [];
  isWebcamOn = false;
  scanned$$ = new Subject<string>();
  triggerCapture$$: Subject<any> = new Subject();

  get isMultipleCodesDetected(): boolean {
    return this.detectedBarcodes?.length > 1;
  }

  ngOnInit(): void {
    this.poolBarcodes();
    setInterval(() => {
      if (!this.isWebcamOn) {
        this.isWebcamOn = !!this.webcam?.videoInitialized;
        safeDetectChanges(this.cdr);
      }
      if (this.isWebcamOn) {
        this.captureImage();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.detectSub?.unsubscribe();
  }

  captureImage(): void {
    this.triggerCapture$$.next(void 0);
  }

  @HostListener('window:keydown.escape', ['$event'])
  close(): void {
    this.detectedBarcodes = [];
    this.close$$.next(true);
  }

  processImage(webcamImage: WebcamImage): void {
    fetch(webcamImage.imageAsDataUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], 'File name', { type: 'image/png' });
        const barcodeDetector = new BarcodeDetector();
        barcodeDetector.detect(file).then((detectedBarcodes: DetectedBarcode[]) => {
          if (!detectedBarcodes.length) {
            return;
          }
          const barcodeValues = detectedBarcodes.map((barcode) => barcode?.rawValue);
          this.detectedBarcodes$$.next(barcodeValues);
        });
      });
  }

  resetScanning(): void {
    this.detectedBarcodes = [];
    this.poolBarcodes();
    safeDetectChanges(this.cdr);
  }

  selectValue(value: string): void {
    this.outputScannedText(value);
    this.close();
  }

  private outputScannedText(text: string): void {
    this.scanned$$.next(text);
  }

  /**
   * Starts pooling detected unique barcodes so that if there's more than one in close proximity, they can be
   * presented to the user.
   */
  private poolBarcodes(): void {
    this.detectSub = this.detectedBarcodes$$
      .pipe(
        scan((currDetected: string[], newDetected: string[]) => {
          return Array.from(new Set(currDetected.concat(newDetected)));
        }, []),
        distinctUntilChanged((prev, curr) => isDeepEqual(prev, curr)),
        debounceTime(500),
        first((barcodes) => !!barcodes.length),
      )
      .subscribe((barcodes) => {
        this.detectedBarcodes = barcodes || [];
        if (this.detectedBarcodes?.length === 1) {
          // Detected only one value
          const scannedText = barcodes[0];
          this.outputScannedText(scannedText);
          this.close();
        }
        safeDetectChanges(this.cdr);
      });
  }
}
